import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, LoadingIndicator } from '@qga/roo-ui/components';
import GoogleMapReact from 'google-map-react';
import PropTypes from 'prop-types';

const renderMarkers = ({ map, maps }, { lat, lng, title }) => {
  const marker = new maps.Marker({
    position: { lat, lng },
    title,
  });

  marker.setMap(map);
};

/**
 * @typedef {Object} LatLng
 * @property {string|number} lat - Latitude coordinate.
 * @property {string|number} lng - Longitude coordinate.
 */

/**
 * @typedef {Object} Pin
 * @property {string} [title] - Title of the pin.
 * @property {string|number} [lat] - Latitude of the pin.
 * @property {string|number} [lng] - Longitude of the pin.
 */

/**
 * @typedef {Object} GoogleMapProps
 * @property {string} apiKey - Google Maps API key. (required)
 * @property {import('react').ReactNode} [children] - Children nodes.
 * @property {LatLng} center - Center coordinates of the map. (required)
 * @property {number} zoom - Zoom level of the map. (required)
 * @property {Pin[]} [pins] - Array of pin objects to place on the map.
 * @property {function} [onGoogleApiLoaded] - Callback function executed when Google API is loaded.
 * @property {Object} [options] - Additional Google Maps options.
 */

/**
 * @param {GoogleMapProps} props
 */
const GoogleMap = ({
  apiKey,
  center,
  zoom,
  children,
  pins = [],
  onGoogleApiLoaded,
  options,
}) => {
  const ref = useRef();
  const [intersected, setIntersected] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return () => {};
    }

    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (!isIntersecting) {
        return;
      }

      setIntersected(true);
    });

    observer.observe(el);

    return () => observer.unobserve(el);
  }, []);

  return (
    <Box position="relative" height="100%" data-testid="GOOGLE_MAP" ref={ref}>
      <Flex
        alignItems="center"
        justifyContent="center"
        bg="greys.porcelain"
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        left="0"
      >
        <LoadingIndicator />
      </Flex>
      {intersected && (
        <GoogleMapReact
          bootstrapURLKeys={{ key: apiKey }}
          defaultCenter={center}
          defaultZoom={zoom}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={(googleMap) => {
            pins.map((pin) => renderMarkers(googleMap, pin));

            if (onGoogleApiLoaded) {
              onGoogleApiLoaded(googleMap);
            }
          }}
          options={{
            streetViewControl: true,
            mapTypeControl: true,
            ...options,
          }}
        >
          {children}
        </GoogleMapReact>
      )}
    </Box>
  );
};

GoogleMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  children: PropTypes.node,
  center: PropTypes.shape({
    lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  zoom: PropTypes.number.isRequired,
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  onGoogleApiLoaded: PropTypes.func,
  options: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default GoogleMap;
